import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useRiqueChatStore } from '@/stores/riqueChatStore';
import {
  createRiqueConversation,
  deleteAllRiqueConversations,
  deleteRiqueConversation,
  fetchRiqueConversations,
  fetchRiqueMessages,
  fetchRiqueStatus,
  sendRiqueMessage,
  type RiqueMessage,
} from '@/lib/riqueApi';

export function useRiqueChat(enabled: boolean) {
  const queryClient = useQueryClient();
  const companyId = useRiqueChatStore((s) => s.companyId);
  const pageContext = useRiqueChatStore((s) => s.pageContext);
  const isOpen = useRiqueChatStore((s) => s.isOpen);
  const consumePendingPrompt = useRiqueChatStore((s) => s.consumePendingPrompt);

  const [activeId, setActiveId] = useState<string | null>(null);
  const [messages, setMessages] = useState<RiqueMessage[]>([]);
  const [streaming, setStreaming] = useState('');
  const [loadingReply, setLoadingReply] = useState(false);

  const active = enabled && isOpen;

  const { data: status } = useQuery({
    queryKey: ['rique-status'],
    queryFn: fetchRiqueStatus,
    staleTime: 60_000,
    enabled: enabled,
  });

  const { data: companies } = useQuery({
    queryKey: ['rique-companies'],
    queryFn: async () => {
      const res = await api.get('/companies?page=1&limit=500');
      const payload = res?.data?.data ?? res?.data ?? [];
      if (Array.isArray(payload?.companies)) return payload.companies as Array<{ id: string; name: string }>;
      if (Array.isArray(payload)) return payload;
      return [];
    },
    enabled: active,
  });

  const { data: conversations = [], refetch: refetchConversations } = useQuery({
    queryKey: ['rique-conversations'],
    queryFn: fetchRiqueConversations,
    enabled: active,
  });

  const activeConversation = useMemo(
    () => conversations.find((c) => c.id === activeId),
    [conversations, activeId]
  );

  const lastAssistant = useMemo(() => {
    const assistants = messages.filter((m) => m.role === 'ASSISTANT');
    return assistants[assistants.length - 1]?.content;
  }, [messages]);

  const unavailable = !status?.enabled || !status?.openAiConfigured;

  const loadMessages = useCallback(async (conversationId: string) => {
    const data = await fetchRiqueMessages(conversationId);
    setMessages(data);
  }, []);

  useEffect(() => {
    if (activeId && active) void loadMessages(activeId);
    else if (!activeId) setMessages([]);
  }, [activeId, loadMessages, active]);

  useEffect(() => {
    if (conversations.length > 0 && !activeId && active) {
      setActiveId(conversations[0].id);
    }
  }, [conversations, activeId, active]);

  const handleSend = useCallback(
    async (content: string, overrideId?: string) => {
      let convId = overrideId ?? activeId;
      if (!convId) {
        const conv = await createRiqueConversation(companyId || undefined);
        convId = conv.id;
        setActiveId(conv.id);
        await refetchConversations();
      }
      const userMsg: RiqueMessage = {
        id: `temp-${Date.now()}`,
        role: 'USER',
        content,
        createdAt: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, userMsg]);
      setLoadingReply(true);
      setStreaming('');
      try {
        const result = await sendRiqueMessage(convId, content, (chunk) => {
          setStreaming((prev) => prev + chunk);
        }, pageContext || undefined);
        setMessages((prev) => [
          ...prev,
          {
            id: result.messageId || `assistant-${Date.now()}`,
            role: 'ASSISTANT',
            content: result.content,
            createdAt: new Date().toISOString(),
            metadata: result.metadata,
          },
        ]);
        setStreaming('');
        await refetchConversations();
        void queryClient.invalidateQueries({ queryKey: ['rique-conversations'] });
      } finally {
        setLoadingReply(false);
      }
    },
    [activeId, companyId, pageContext, queryClient, refetchConversations]
  );

  const handleNewConversation = useCallback(async () => {
    const conv = await createRiqueConversation(companyId || undefined);
    await refetchConversations();
    setActiveId(conv.id);
    setMessages([]);
    return conv.id;
  }, [companyId, refetchConversations]);

  const handleDeleteConversation = useCallback(
    async (conversationId: string) => {
      await deleteRiqueConversation(conversationId);
      const remaining = conversations.filter((c) => c.id !== conversationId);
      if (activeId === conversationId) {
        const next = remaining[0]?.id ?? null;
        setActiveId(next);
        if (!next) setMessages([]);
      }
      await refetchConversations();
      void queryClient.invalidateQueries({ queryKey: ['rique-conversations'] });
    },
    [activeId, conversations, queryClient, refetchConversations]
  );

  const handleDeleteAllConversations = useCallback(async () => {
    await deleteAllRiqueConversations();
    setActiveId(null);
    setMessages([]);
    await refetchConversations();
    void queryClient.invalidateQueries({ queryKey: ['rique-conversations'] });
  }, [queryClient, refetchConversations]);

  useEffect(() => {
    if (!active || unavailable) return;
    const prompt = consumePendingPrompt();
    if (!prompt) return;

    void (async () => {
      const convId = activeId ?? (await handleNewConversation());
      await handleSend(prompt, convId);
    })();
  }, [active, unavailable, consumePendingPrompt, activeId, handleNewConversation, handleSend]);

  return {
    status,
    companies,
    conversations,
    activeId,
    setActiveId,
    activeConversation,
    messages,
    streaming,
    loadingReply,
    lastAssistant,
    unavailable,
    handleSend,
    handleNewConversation,
    handleDeleteConversation,
    handleDeleteAllConversations,
  };
}
